/**
 * AdminScopeLocked — «Lås opp»-flaten for en admin-liste som er låst bak
 * requireAdminScope (D-04).
 *
 * En GET bak requireAdminScope svarer 403 `admin_scope_required` /
 * `admin_scope_expired` til adminen har tastet sitt eget passord på nytt (#143). En
 * <ErrorState> med «Prøv igjen» er da en blindvei — samme kall feiler igjen —
 * så seksjonen viser denne i stedet. Ved vellykket re-auth invaliderer vi alle
 * spørringer, slik at alle admin-seksjonene på siden henter på nytt samtidig.
 *
 * Kallere avgjør med `isAdminScopeError(query.error)` når den skal vises;
 * andre feil skal fortsatt gå til den generiske <ErrorState>.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { AdminReauthModal } from './AdminReauthModal';

export interface AdminScopeLockedProps {
  /** Prefiks for test-id-ene: `${testIdPrefix}-scope-locked` / `-unlock`. */
  testIdPrefix: string;
  /** Hva som låses opp — allerede oversatt tekst. */
  message: string;
}

export function AdminScopeLocked({ testIdPrefix, message }: AdminScopeLockedProps): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid={`${testIdPrefix}-scope-locked`}
      className={`${GLASS_SURFACE} flex flex-col items-start gap-3 rounded-lg p-4`}
    >
      <p className="text-sm text-ink-2">{message}</p>
      <Button data-testid={`${testIdPrefix}-unlock`} onClick={() => setOpen(true)}>
        {t('admin.members.unlock')}
      </Button>
      <AdminReauthModal
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={() => {
          setOpen(false);
          void qc.invalidateQueries();
        }}
      />
    </div>
  );
}
